import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Camera,
  Clapperboard,
  FileText,
  Image as ImageIcon,
  Layers,
  ShieldCheck,
  Sparkles,
  Video,
  Wand2,
} from "lucide-react";

import type { EchoflicksSessionMedia, EchoflicksVideoSession } from "../types";
import { EmptyHint } from "./EchoflicksSessionList";
import {
  deriveSceneBreakdown,
  parallaxMoveLabel,
  revealLabel,
  type SceneBreakdownEntry,
} from "./sceneBreakdown";
import { isImage, isVideo } from "./sessionUtils";
import { useMediaUrl } from "./useMediaUrl";
import { useScriptEnvelope } from "./useScriptEnvelope";
import { DisplayCropBadge } from "./DisplayCropBadge";

/**
 * Per-scene "how was this shot made" view: the camera motion, the generation prompt and the
 * production context (animated via Kling i2v? auto-promoted? which source photo?) for every
 * scene in the delivered Echo. Reads the orchestrator's structured `diagnostics.scene_breakdown`
 * when present, and otherwise falls back to deriving the same shape from the raw script scenes
 * (splitting the camera direction back out of the prompt) so it also works for older Echoes.
 */

export const SceneBreakdownPanel = ({
  session,
  media,
}: {
  session: EchoflicksVideoSession;
  media: EchoflicksSessionMedia[];
}) => {
  const { state, scriptUrl, scriptUrlState } = useScriptEnvelope(
    session,
    media,
  );
  const mediaByFilename = useMemo(() => buildMediaMap(media), [media]);

  if (!scriptUrl && scriptUrlState.isLoading) {
    return <PanelShell hint="Preparing a private signed script URL..." />;
  }
  if (!scriptUrl && scriptUrlState.status === "error") {
    return <PanelShell hint={scriptUrlState.error} />;
  }
  if (!scriptUrl) {
    return <PanelShell hint="No script JSON is linked to this Echo yet." />;
  }
  if (state.status === "loading") {
    return <PanelShell hint="Loading scene breakdown..." />;
  }
  if (state.status === "error") {
    return <PanelShell hint={state.message} />;
  }
  if (state.status !== "loaded") {
    return null;
  }

  const scenes = deriveSceneBreakdown(state.envelope);
  if (scenes.length === 0) {
    return <PanelShell hint="No scenes were found in this script." />;
  }

  const animated = scenes.filter((scene) => scene.animated).length;
  const withMove = scenes.filter(
    (scene) => scene.cameraMove || scene.cameraDirection,
  ).length;
  const promoted = scenes.filter((scene) => scene.promoted).length;
  const parallax = scenes.filter((scene) => scene.parallax).length;
  const anchored = scenes.filter((scene) => scene.reveal).length;
  const guarded = scenes.filter((scene) => scene.cameraRevealGuard).length;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Clapperboard className="size-5" />
          <CardTitle>Scene Breakdown</CardTitle>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          How each scene was made — camera motion, generation prompt and
          production context.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Badge variant="secondary">{scenes.length} scenes</Badge>
          <Badge variant="outline">{animated} animated (i2v)</Badge>
          <Badge variant="outline">{withMove} with a camera move</Badge>
          {promoted > 0 ? (
            <Badge variant="outline">{promoted} auto-animated</Badge>
          ) : null}
          {parallax > 0 ? (
            <Badge variant="outline" className="gap-1">
              <Layers className="size-3" /> {parallax} parallax
            </Badge>
          ) : null}
          {anchored > 0 ? (
            <Badge variant="outline" className="gap-1">
              <ShieldCheck className="size-3" /> {anchored} end on the full
              photo
            </Badge>
          ) : null}
          {guarded > 0 ? (
            <Badge variant="outline" className="gap-1">
              <ShieldCheck className="size-3" /> {guarded} kept inside the photo
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {scenes.map((scene) => (
            <SceneBreakdownRow
              key={`${scene.sceneId ?? scene.index}-${scene.type ?? "scene"}`}
              scene={scene}
              media={scene.file ? mediaByFilename.get(scene.file) : undefined}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const PanelShell = ({ hint }: { hint?: string }) => (
  <Card>
    <CardHeader>
      <div className="flex items-center gap-2">
        <Clapperboard className="size-5" />
        <CardTitle>Scene Breakdown</CardTitle>
      </div>
    </CardHeader>
    <CardContent>
      <EmptyHint>{hint ?? "Scene breakdown is unavailable."}</EmptyHint>
    </CardContent>
  </Card>
);

const SceneBreakdownRow = ({
  scene,
  media,
}: {
  scene: SceneBreakdownEntry;
  media?: EchoflicksSessionMedia;
}) => (
  <div className="grid gap-3 rounded-md border p-3 md:grid-cols-[160px_minmax(0,1fr)]">
    <SceneThumb scene={scene} media={media} />
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge>{scene.sceneId ?? scene.index + 1}</Badge>
        <Badge variant="secondary">{humanizeType(scene.type)}</Badge>
        {scene.animated ? (
          <Badge variant="outline" className="gap-1">
            <Sparkles className="size-3" /> i2v
          </Badge>
        ) : null}
        {scene.promoted ? (
          <Badge variant="outline" className="gap-1">
            <Wand2 className="size-3" /> Auto-animated
          </Badge>
        ) : null}
        {scene.parallax ? (
          <Badge className="gap-1">
            <Layers className="size-3" /> Parallax
            {parallaxMoveLabel(scene.cameraMove)
              ? ` · ${parallaxMoveLabel(scene.cameraMove)}`
              : ""}
          </Badge>
        ) : null}
        {scene.length ? (
          <Badge variant="outline">{formatSeconds(scene.length)}</Badge>
        ) : null}
        {scene.transition ? (
          <Badge variant="outline">
            {scene.transition.replaceAll("_", " ")}
          </Badge>
        ) : null}
      </div>

      <CameraMotion scene={scene} />

      <div>
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <FileText className="size-3.5" /> Generation prompt
        </div>
        {scene.basePrompt ? (
          <p className="mt-1 whitespace-pre-wrap text-sm">{scene.basePrompt}</p>
        ) : scene.titleText ? (
          <p className="mt-1 text-sm font-medium">{scene.titleText}</p>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">
            No generation prompt for this scene.
          </p>
        )}
      </div>

      {scene.file ? (
        <div className="text-xs text-muted-foreground">
          Source: {scene.file}
        </div>
      ) : null}
    </div>
  </div>
);

const CameraMotion = ({ scene }: { scene: SceneBreakdownEntry }) => {
  const hasMotion = Boolean(scene.cameraMove || scene.cameraDirection);
  return (
    <div className="rounded-md bg-muted/50 p-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Camera className="size-3.5" /> Camera motion
      </div>
      {hasMotion ? (
        <div className="mt-2 space-y-1.5">
          {scene.cameraMove ? (
            <Badge className="gap-1">
              <Camera className="size-3" /> {humanizeMove(scene.cameraMove)}
            </Badge>
          ) : (
            <Badge variant="secondary">Custom move</Badge>
          )}
          {scene.parallax &&
          (scene.parallaxAnchor || scene.parallaxBackground) ? (
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Layers className="size-3" />
              Depth reveal
              {scene.parallaxAnchor ? (
                <>
                  {" — "}
                  <span className="font-medium">{scene.parallaxAnchor}</span>
                </>
              ) : null}
              {scene.parallaxBackground ? (
                <>
                  {" → "}
                  <span className="font-medium">
                    {scene.parallaxBackground}
                  </span>
                </>
              ) : null}
              {scene.parallaxSource ? ` (${scene.parallaxSource})` : null}
            </p>
          ) : null}
          {scene.cameraDirection ? (
            <p className="text-sm text-muted-foreground">
              {scene.cameraDirection}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="mt-1 text-sm text-muted-foreground">
          {scene.type === "TitleCardScene"
            ? "Title card — no camera move."
            : "Static shot — no camera move applied."}
        </p>
      )}
      <div className="mt-2">
        <CameraRevealNotes scene={scene} />
      </div>
    </div>
  );
};

/** CPB: how the camera was kept inside the photo (anchored move or B2C guard rewrite). */
const CameraRevealNotes = ({ scene }: { scene: SceneBreakdownEntry }) => {
  const label = revealLabel(scene.reveal);
  const guard = scene.cameraRevealGuard;
  if (!label && !guard) return null;
  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      {label ? (
        <p className="flex items-center gap-1">
          <ShieldCheck className="size-3" /> {label}
          {scene.reveal?.moveId
            ? ` (${humanizeMove(scene.reveal.moveId)})`
            : null}
        </p>
      ) : null}
      {guard ? (
        <p className="flex flex-wrap items-center gap-1">
          <ShieldCheck className="size-3" /> Kept inside the photo
          {guard.revealClass
            ? ` (${guard.revealClass.replaceAll("_", " ")})`
            : null}
          :
          {guard.from ? (
            <span className="line-through">{guard.from}</span>
          ) : null}
          {guard.to ? (
            <span className="font-medium text-foreground">{guard.to}</span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
};

const SceneThumb = ({
  scene,
  media,
}: {
  scene: SceneBreakdownEntry;
  media?: EchoflicksSessionMedia;
}) => {
  const mediaUrl = useMediaUrl(media);

  if (mediaUrl.url && isImage(media?.mime_type)) {
    return (
      <div className="relative">
        <img
          src={mediaUrl.url}
          alt={media?.s3_key ?? "Scene source"}
          className="aspect-video w-full rounded-md bg-black object-contain"
          loading="lazy"
          onError={mediaUrl.refresh}
        />
        <DisplayCropBadge label="Full frame · contain" isDisplayCrop={false} />
      </div>
    );
  }
  if (mediaUrl.url && isVideo(media?.mime_type)) {
    return (
      <video
        src={mediaUrl.url}
        className="aspect-video w-full rounded-md bg-black object-contain"
        controls
        onError={mediaUrl.refresh}
      />
    );
  }
  if (scene.type === "TitleCardScene") {
    return (
      <div className="flex aspect-video items-center justify-center rounded-md bg-primary p-3 text-center text-sm font-semibold text-primary-foreground">
        {scene.titleText ?? "Title"}
      </div>
    );
  }
  return (
    <div className="flex aspect-video items-center justify-center rounded-md bg-muted">
      {scene.type === "VideoScene" ? (
        <Video className="size-5 text-muted-foreground" />
      ) : (
        <ImageIcon className="size-5 text-muted-foreground" />
      )}
    </div>
  );
};

function buildMediaMap(media: EchoflicksSessionMedia[]) {
  const byFilename = new Map<string, EchoflicksSessionMedia>();
  for (const item of media) {
    const filename =
      item.s3_key?.split("/").at(-1) ?? item.local_path?.split(/[\\/]/).at(-1);
    if (filename && !byFilename.has(filename)) {
      byFilename.set(filename, item);
    }
  }
  return byFilename;
}

function humanizeMove(name: string) {
  const cleaned = name.replaceAll("_", " ").trim();
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : name;
}

function humanizeType(type: string | null) {
  if (!type) return "Scene";
  return type.replace(/Scene$/, "").replace(/([a-z])([A-Z])/g, "$1 $2");
}

function formatSeconds(seconds: number) {
  if (seconds < 60)
    return `${Number.isInteger(seconds) ? seconds : seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}
